// The single source of truth for which assignments each flow component type
// requires. Every QP-146 consumer reads from here so the web picker, the
// server-side requirement guard, the activation guard, and the template dialog
// can never drift onto divergent per-component requirement tables:
//   - web COMPONENT_METADATA (display/layout only; requirement flags sourced here)
//   - the API requirement-guard service (from-template create, activation, update)
//
// The asset-allocation / timeseries portion is NOT declared here — it is derived
// from REQUIREMENT_POLICY in `collection-requirements.ts`, which stays the single
// authority for the setting -> component-type relationship.
//
// Pure module: no I/O, no dependencies beyond `./collection-requirements`.

import {
  type CollectionRequirementSettings,
  getSettingForComponentType,
} from "./collection-requirements";

/** The five assignment requirements a flow component type can declare. */
export type ComponentRequirementFlags = {
  requiresRiskModel: boolean;
  requiresPortfolioCollection: boolean;
  requiresFinancialSituationModel: boolean;
  requiresAssetAllocation: boolean;
  requiresTimeseries: boolean;
};

/** The three flags owned by this module's base table (the two collection-setting
 * flags are derived from REQUIREMENT_POLICY, never listed here). */
type BaseRequirementFlag =
  | "requiresRiskModel"
  | "requiresPortfolioCollection"
  | "requiresFinancialSituationModel";

/**
 * Per-component requirements NOT governed by a collection setting. `risk_assessment`
 * is the sole driver of `requiresRiskModel`: portfolio-collection-needing components
 * do not independently flag a risk model, since the collection picker's
 * risk-model-first dependency already forces one whenever a collection is required.
 *
 * These string literals are the canonical contract, matching the corresponding
 * members of the web `ComponentType` union (apps/web/src/lib/types/flow.ts).
 * Component types not listed here require none of these three.
 */
const BASE_REQUIREMENT_TABLE: Record<string, readonly BaseRequirementFlag[]> = {
  risk_assessment: ["requiresRiskModel"],
  financial_situation: ["requiresFinancialSituationModel"],
  portfolio_display: ["requiresPortfolioCollection"],
  portfolio_xray: ["requiresPortfolioCollection"],
  asset_allocation: ["requiresPortfolioCollection"],
  portfolio_forecast: ["requiresPortfolioCollection"],
  historical_return: ["requiresPortfolioCollection"],
  expected_value: ["requiresPortfolioCollection"],
  // QL-315 — the asset classes it segments by are collection-scoped rows.
  asset_class_forecast: ["requiresPortfolioCollection"],
  portfolio_input: ["requiresPortfolioCollection"],
  custom_portfolio: ["requiresPortfolioCollection"],
  cost_table: ["requiresPortfolioCollection"],
  cost_chart: ["requiresPortfolioCollection"],
  portfolio_costs: ["requiresPortfolioCollection"],
  product_costs: ["requiresPortfolioCollection"],
  // QL-416 — product cost values, fee rows and FX fees are collection-scoped;
  // the return/volatility metrics need the risk model the collection implies.
  cost_benefit: ["requiresPortfolioCollection"],
  // QP-179 — sustainability lives on the portfolio collection.
  sustainability: ["requiresPortfolioCollection"],
  sustainability_status: ["requiresPortfolioCollection"],
  // QP-224 — client preference definitions live on the portfolio collection.
  client_preferences: ["requiresPortfolioCollection"],
  // QL-314 — volatility computed from asset class stats + correlations.
  volatility_chart: ["requiresPortfolioCollection"],
  // QL-313 — equity share blended from the collection's asset-class equitySharePct.
  equity_share_chart: ["requiresPortfolioCollection"],
  // QL-375 — mandate templates, rules and the goal-type three-way live on the collection.
  mandate: ["requiresPortfolioCollection"],
  mandate_comparison: ["requiresPortfolioCollection"],
  // QL-207 — lists each portfolio's amounts and payment schedule. In the
  // advisor and client flows only portfolio_input (itself collection-bound)
  // fills those in; the public API and AI tools can set them without one, but
  // the ticket keeps portfolio components off flows that have no portfolios.
  portfolio_list: ["requiresPortfolioCollection"],
  // QL-308 — the product types it assesses are configured on the collection;
  // without one the widget can only say "No product types configured".
  product_assessment: ["requiresPortfolioCollection"],
};

/** The requirement flags for a single component type. Unknown types require nothing. */
export function getComponentRequirements(componentType: string): ComponentRequirementFlags {
  const base = BASE_REQUIREMENT_TABLE[componentType] ?? [];
  const setting = getSettingForComponentType(componentType);
  return {
    requiresRiskModel: base.includes("requiresRiskModel"),
    requiresPortfolioCollection: base.includes("requiresPortfolioCollection"),
    requiresFinancialSituationModel: base.includes("requiresFinancialSituationModel"),
    requiresAssetAllocation: setting === "requiresAssetAllocation",
    requiresTimeseries: setting === "requiresTimeseries",
  };
}

/** All five requirement flags, in evaluation order. */
const REQUIREMENT_FLAGS = [
  "requiresRiskModel",
  "requiresPortfolioCollection",
  "requiresFinancialSituationModel",
  "requiresAssetAllocation",
  "requiresTimeseries",
] as const satisfies readonly (keyof ComponentRequirementFlags)[];

/**
 * The aggregate requirements of a collection of component types: which flags are
 * needed at all, and which component type(s) are responsible for each flag.
 */
export type RequirementSet = {
  flags: ComponentRequirementFlags;
  responsibleComponents: Record<keyof ComponentRequirementFlags, string[]>;
};

/** OR-reduce the per-component flags across a list, tracking responsible components. */
export function deriveRequirementSet(componentTypes: Iterable<string>): RequirementSet {
  const flags: ComponentRequirementFlags = {
    requiresRiskModel: false,
    requiresPortfolioCollection: false,
    requiresFinancialSituationModel: false,
    requiresAssetAllocation: false,
    requiresTimeseries: false,
  };
  const responsibleComponents: Record<keyof ComponentRequirementFlags, string[]> = {
    requiresRiskModel: [],
    requiresPortfolioCollection: [],
    requiresFinancialSituationModel: [],
    requiresAssetAllocation: [],
    requiresTimeseries: [],
  };

  for (const componentType of componentTypes) {
    const componentFlags = getComponentRequirements(componentType);
    for (const flag of REQUIREMENT_FLAGS) {
      if (componentFlags[flag] && !responsibleComponents[flag].includes(componentType)) {
        flags[flag] = true;
        responsibleComponents[flag].push(componentType);
      }
    }
  }

  return { flags, responsibleComponents };
}

/** The kinds of unmet requirement an evaluation can report. */
export type RequirementViolationKind =
  | "missing_risk_model"
  | "missing_portfolio_collection"
  | "missing_financial_situation_model"
  | "collection_missing_asset_allocation"
  | "collection_missing_timeseries";

/** A single unmet requirement and the component type(s) responsible for it. */
export type RequirementViolation = {
  kind: RequirementViolationKind;
  componentTypes: string[];
};

export type RequirementEvaluationResult = {
  ok: boolean;
  violations: RequirementViolation[];
};

/** The assigned model references and collection settings a flow currently has. */
export type RequirementEvaluationInput = {
  componentTypes: string[];
  references: {
    riskModelId?: string | null;
    portfolioCollectionId?: string | null;
    financialSituationModelId?: string | null;
  };
  /** The assigned collection's requirement settings, or `null` if no collection is assigned. */
  collectionSettings: CollectionRequirementSettings | null;
};

/**
 * Given the present component types, the assigned references, and the assigned
 * collection's settings, return every unmet requirement.
 *
 * The two collection-setting checks only fire when a collection IS assigned — when
 * it is not, `missing_portfolio_collection` already covers those components.
 */
export function evaluateRequirements(
  input: RequirementEvaluationInput,
): RequirementEvaluationResult {
  const { references, collectionSettings } = input;
  const { flags, responsibleComponents } = deriveRequirementSet(input.componentTypes);
  const violations: RequirementViolation[] = [];

  if (flags.requiresRiskModel && !references.riskModelId) {
    violations.push({
      kind: "missing_risk_model",
      componentTypes: responsibleComponents.requiresRiskModel,
    });
  }

  if (flags.requiresPortfolioCollection && !references.portfolioCollectionId) {
    violations.push({
      kind: "missing_portfolio_collection",
      componentTypes: responsibleComponents.requiresPortfolioCollection,
    });
  }

  if (flags.requiresFinancialSituationModel && !references.financialSituationModelId) {
    violations.push({
      kind: "missing_financial_situation_model",
      componentTypes: responsibleComponents.requiresFinancialSituationModel,
    });
  }

  if (
    flags.requiresAssetAllocation &&
    references.portfolioCollectionId &&
    collectionSettings &&
    !collectionSettings.requiresAssetAllocation
  ) {
    violations.push({
      kind: "collection_missing_asset_allocation",
      componentTypes: responsibleComponents.requiresAssetAllocation,
    });
  }

  if (
    flags.requiresTimeseries &&
    references.portfolioCollectionId &&
    collectionSettings &&
    !collectionSettings.requiresTimeseries
  ) {
    violations.push({
      kind: "collection_missing_timeseries",
      componentTypes: responsibleComponents.requiresTimeseries,
    });
  }

  return { ok: violations.length === 0, violations };
}
